import { DB } from '@services/database.service';
import { supabase, getActiveSchema } from '@/supabaseClient';
import { ProgramCompute } from '@shared/logic/program-logic';

/**
 * PROGRAM ORCHESTRATOR
 * Handles complex async I/O for program management (Deep hydration, Multi-table saving).
 */
export const ProgramOrchestrator = {
    /**
     * Fetches and hydrates full program details for the editor.
     */
    async fetchProgramDetails(programId: string) {
        const { data: program } = await supabase
            .schema(getActiveSchema())
            .from('programs')
            .select('*')
            .eq('id', programId)
            .maybeSingle();
            
        if (!program) throw new Error("Program not found");

        const { data: days } = await DB.fetchDeepProgram(programId);
        return ProgramCompute.hydrateProgramForEditor(program, days || []);
    },

    /**
     * Master Save Logic: Performs nested upserts for full program state.
     */
    async saveProgram(programName: string, days: any[], existingProgramId: string | null = null) {
        const { data: userData } = await supabase.auth.getUser();
        const user = userData.user;
        
        const programPayload: any = { 
            name: programName, 
            user_id: user?.id, 
            cycle_length: days.length 
        };
        if (existingProgramId) programPayload.id = existingProgramId;

        const { data: program } = await DB.upsertProgram(programPayload);
        if (!program) throw new Error("Failed to upsert program");

        const { data: library } = await DB.fetchExerciseLibrary();
        const { data: existingDays } = await DB.fetchProgramDays(program.id);
        const existingDayIds = existingDays?.map((d: any) => d.id) || [];

        for (const day of days) {
            const { data: progDay } = await supabase.schema(getActiveSchema()).from('program_days').upsert([{
                id: day.id.length > 30 ? day.id : undefined,
                program_id: program.id, 
                label: day.label, 
                sequence_number: day.sequence_number
            }]).select().single();

            if (!progDay) continue;

            const { data: session } = await supabase.schema(getActiveSchema()).from('sessions').upsert([{
                program_day_id: progDay.id, name: `${day.label} SESSION`
            }], { onConflict: 'program_day_id' }).select().single();

            if (!session) continue;

            const { data: existingBlocks } = await supabase.schema(getActiveSchema()).from('blocks').select('id').eq('session_id', session.id);
            const existingBlockIds = existingBlocks?.map((b: any) => b.id) || [];

            for (const block of day.blocks) {
                const { data: v3Block } = await supabase.schema(getActiveSchema()).from('blocks').upsert([{
                    id: block.id.length > 30 ? block.id : undefined,
                    session_id: session.id, 
                    label: block.label, 
                    block_type: block.block_type, 
                    sort_order: block.sort_order
                }]).select().single();

                if (!v3Block) continue;

                const { data: existingItems } = await supabase.schema(getActiveSchema()).from('block_items').select('id').eq('session_block_id', v3Block.id);
                const existingItemIds = existingItems?.map((i: any) => i.id) || [];

                for (const item of block.items) {
                    const libItem = (library || []).find((l: any) => l.name === item.name);
                    if (!libItem) continue;
                    await supabase.schema(getActiveSchema()).from('block_items').upsert([{
                        id: item.id.length > 30 ? item.id : undefined,
                        session_block_id: v3Block.id,
                        exercise_library_id: libItem.id,
                        target_sets: parseInt(item.target_sets),
                        target_reps: String(item.target_reps),
                        target_weight: String(item.target_weight),
                        target_rpe: String(item.target_rpe),
                        tempo: String(item.tempo),
                        metric_type: item.metric_type,
                        sort_order: item.sort_order,
                        set_targets: item.set_targets
                    }]);
                }
                const itemsToDelete = existingItemIds.filter(id => !day.blocks.some((b: any) => b.items.some((i: any) => i.id === id)));
                if (itemsToDelete.length > 0) await supabase.schema(getActiveSchema()).from('block_items').delete().in('id', itemsToDelete);
            }
            const blocksToDelete = existingBlockIds.filter(id => !day.blocks.some((b: any) => b.id === id));
            if (blocksToDelete.length > 0) await supabase.schema(getActiveSchema()).from('blocks').delete().in('id', blocksToDelete);
        }
        
        const finalIncomingDayIds = days.map(d => d.id);
        const daysToDelete = existingDayIds.filter((id: string) => !finalIncomingDayIds.includes(id));
        if (daysToDelete.length > 0) await supabase.schema(getActiveSchema()).from('program_days').delete().in('id', daysToDelete);

        return true;
    }
};
